import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Clock, BookOpen, Lightbulb, Target, PenLine, Loader2, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { learnLesson, learnComplete } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";

const Block = ({ icon: Icon, label, color, children, testid }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={testid}>
    <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color }}>
      <Icon size={14} /> {label}
    </p>
    <p className="text-[15px] leading-relaxed text-[#e8e6df]">{children}</p>
  </div>
);

export const LessonModal = ({ lessonId, isDone, onClose, onCompleted }) => {
  const { user, requireAuth } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    if (!lessonId) return;
    setLesson(null); setCelebrate(false);
    learnLesson(lessonId).then(setLesson).catch(() => toast.error("Could not load lesson"));
  }, [lessonId]);

  const complete = () => {
    const run = async () => {
      setSaving(true);
      try {
        const prog = await learnComplete(lessonId);
        setCelebrate(true);
        onCompleted?.(prog);
        setTimeout(() => { setCelebrate(false); onClose(); }, 1700);
      } catch { toast.error("Could not save progress"); }
      finally { setSaving(false); }
    };
    if (user) run(); else requireAuth(run);
  };

  if (!lessonId) return null;

  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="lesson-modal">
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl bg-gradient-to-b from-[#161514] to-[#0F0F0F] text-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white z-10" data-testid="lesson-close"><X size={22} /></button>

        {celebrate ? (
          <div className="p-12 flex flex-col items-center justify-center text-center min-h-[420px]" data-testid="lesson-celebrate">
            <div className="w-20 h-20 rounded-full bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center animate-float"><CheckCircle2 size={40} /></div>
            <p className="font-display text-3xl font-extrabold mt-6">Lesson Complete</p>
            <p className="text-[#D4AF37] font-bold text-lg mt-2 flex items-center gap-1.5"><Sparkles size={18} /> +{lesson?.xp || 25} Founder XP</p>
            <p className="text-white/50 text-sm mt-3">Next lesson unlocked.</p>
          </div>
        ) : !lesson ? (
          <div className="p-12 flex items-center justify-center min-h-[420px]"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>
        ) : (
          <div className="p-7">
            <div className="flex items-center gap-2 text-xs text-white/60">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1"><Clock size={12} /> 60 seconds</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1">{lesson.difficulty}</span>
            </div>
            <h2 className="font-display text-3xl font-extrabold mt-4">{lesson.title}</h2>

            <div className="mt-5 space-y-3">
              <Block icon={BookOpen} label="Definition" color="#D4AF37" testid="lesson-definition">{lesson.definition}</Block>
              <Block icon={Lightbulb} label="Example" color="#60a5fa" testid="lesson-example">{lesson.example}</Block>
              <Block icon={Sparkles} label="Founder Tip" color="#34d399" testid="lesson-tip">{lesson.tip}</Block>
              <Block icon={PenLine} label="Your Action" color="#f472b6" testid="lesson-action">{lesson.action}</Block>
            </div>

            <button
              onClick={complete}
              disabled={saving}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
              data-testid="lesson-complete-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : isDone ? <CheckCircle2 size={16} /> : null}
              {isDone ? "Review Complete" : "Complete Lesson"} {!saving && <ArrowRight size={16} />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
